/**
 * ReviewExclusiveSubscriptionCTAFooter
 *
 * Sticky-bottom CTA row for the ReviewExclusiveSubscriptionSheet: Confirm /
 * Reject / Close, in that order. Used inside
 * ReviewExclusiveSubscriptionSheetContent.
 *
 * Mirrors Flutter's `_bottomActions()`
 * (`lib/presentation/bottom_sheet/review_exclusive_subscription_bottom_sheet.dart:266-340`)
 * — a full-width vertical stack of three tappable rows separated by hairline
 * top borders:
 *   - `:274-294` Confirm — `_reviewExclusiveSubscription(true)`, styled
 *     `AppStyles.reviewSubscriptionConfirmButtonStyle` -> `AppColors.blue`
 *     (`lib/presentation/utils/app_styles.dart:1759-1764`;
 *     `lib/domain/constants/app_colors.dart:60`) -> web `theme.blue`.
 *   - `:295-315` Reject — `_reviewExclusiveSubscription(false)`, styled
 *     `AppStyles.reviewSubscriptionDeclineButtonStyle` -> `AppColors.activeRed`
 *     (`app_styles.dart:1766-1771`; `app_colors.dart:19`) -> web `theme.danger`.
 *   - `:316-336` Close — `_closeBottomSheet()`, styled
 *     `AppStyles.bottomSheetActionButtonNegativeStyle` -> default text colour
 *     (`app_styles.dart:1752-1757`) -> `CTAButtons`' default `theme.text.primary`.
 *
 * All three are disabled while `isProcessing`. In Flutter the equivalent is
 * emergent rather than explicit: the filled `Positioned.fill` overlay
 * (`:231-251`) renders over the actions and absorbs their taps, and
 * `_closeBottomSheet` additionally early-returns while busy (`:56-58`).
 *
 * `closeOnClick: false` on Confirm/Reject is load-bearing: the owning content
 * component closes the sheet explicitly *after* awaiting the review call
 * (Flutter `:84`), so letting `CTAButtons` auto-close (`CTAButtons.tsx:57-63`)
 * would double-close and skip the loading overlay entirely.
 *
 * Sanctioned UI deviation: Flutter hand-rolls these rows as `GestureDetector`s
 * with manual hairline borders; the web reuses the shared `CTAButtons`, whose
 * column layout already renders stacked buttons with separators. Parity binds
 * the set of controls, their order, their colour semantics and their states —
 * not the widget implementation (`conventions.md` §Parity calibration).
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { CTAButtons } from '@/presentation/components/common/sheet/service/components/CTAButtons';
import { SheetCTAFooter } from '@/presentation/components/common/sheet/service/components/SheetCTAFooter';

export interface ReviewExclusiveSubscriptionCTAFooterProps {
  /** Mirrors Flutter's `_isPerformingConfirmAction` (`:29`) — disables all actions. */
  isProcessing: boolean;
  /** Flutter `:276` — `_reviewExclusiveSubscription(true)`. */
  onConfirm: () => void;
  /** Flutter `:297` — `_reviewExclusiveSubscription(false)`. */
  onReject: () => void;
  /** Flutter `:318` — `_closeBottomSheet()`. */
  onClose: () => void;
}

export const ReviewExclusiveSubscriptionCTAFooter: React.FC<
  ReviewExclusiveSubscriptionCTAFooterProps
> = ({ isProcessing, onConfirm, onReject, onClose }) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();

  return (
    <SheetCTAFooter>
      <CTAButtons
        cta={{
          buttons: [
            {
              text: l10n.common.confirm,
              onClick: onConfirm,
              qaId: 'review-exclusive-subscription-confirm',
              textColor: theme.blue,
              // No per-button loading state — the sheet-wide overlay covers it.
              disabled: isProcessing,
              // The content component closes AFTER the await (Flutter :84).
              closeOnClick: false,
            },
            {
              text: l10n.reviewExclusiveSubscription.reject,
              onClick: onReject,
              qaId: 'review-exclusive-subscription-reject',
              textColor: theme.danger,
              disabled: isProcessing,
              closeOnClick: false,
            },
            {
              text: l10n.common.close,
              onClick: onClose,
              qaId: 'review-exclusive-subscription-close',
              disabled: isProcessing,
            },
          ],
        }}
        onClose={() => {
          /* CTAButtons handles the auto-close via closeOnClick */
        }}
      />
    </SheetCTAFooter>
  );
};
